import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Paper,
    Typography,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    Chip,
    List,
    ListItem,
    ListItemText,
    Divider,
    CircularProgress,
} from "@mui/material";
import { FiCheck, FiEdit, FiX } from "react-icons/fi";
import { useParams } from "react-router-dom";
import { fetchBanquetBookingDetails, updateBanquetBooking } from "../../../api/banquet";
import { showToast } from "../../../api/toast";
import Breadcrumb from "../../../components/common/Breadcrumb";
import { formatDateMoment, formatDateTime, formatTime } from "../../../api/config";

// Format Time in AM/PM
const formatTimeInIST = (timeStr) => {
    if (!timeStr) return "N/A";
    const todayDate = new Date().toISOString().split("T")[0];
    const timeInUTC = new Date(`${todayDate}T${timeStr}:00Z`);
    const options = {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        timeZone: "Asia/Kolkata",
    };
    return new Intl.DateTimeFormat("en-IN", options).format(timeInUTC);
};

const statusOptions = ["Pending", "Confirmed", "Cancelled"];

const SingleBooking = () => {
    const { id } = useParams();
    const [booking, setBooking] = useState({});
    const [isEditDialogOpen, setEditDialogOpen] = useState(false);
    const [editBooking, setEditBooking] = useState({ bookingId: id, bookingStatus: "" });

    const [loading, setLoading] = useState(false)

    // Fetch booking details
    useEffect(() => {
        getBookingById(id);
    }, [id]);

    const getBookingById = async (bookingId) => {
        try {
            const response = await fetchBanquetBookingDetails(bookingId);
            setBooking(response.data.booking);
            setEditBooking({ bookingStatus: response?.data?.booking?.bookingStatus });
        } catch (error) {
            console.error("Failed to fetch booking details:", error);
            showToast("Failed to fetch booking details. Please try again.", "error");
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditBooking((prev) => ({ ...prev, [name]: value }));
    };

    const handleEditClick = () => setEditDialogOpen(true);
    const handleDialogClose = () => setEditDialogOpen(false);

    const handleSaveChanges = async (newStatus) => {
        setLoading(true)
        try {
            const response = await updateBanquetBooking({
                // bookingId: id,
                // bookingStatus: editBooking.bookingStatus,
                bookingId: id,
                bookingStatus: newStatus,
            });
            if (response.status === 200) {
                getBookingById(id);
                setEditDialogOpen(false);
                showToast("Booking details updated successfully!", "success");
            } else {
                showToast("Failed to update booking details. Please try again.", "error");
            }
        } catch (error) {
            console.error("Failed to update booking details:", error);
            showToast("Failed to update booking details. Please try again.", "error");
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box
                sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    backgroundColor: "rgba(255, 255, 255, 0.7)",
                    zIndex: 1000,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box sx={{ pt: "80px", pb: "20px" }}>
            <Breadcrumb />
            <Typography variant="h4" sx={{ mb: 2, color: "primary.main", fontWeight: "bold" }}>
                Booking Details
            </Typography>
            <Paper
                sx={{
                    p: 4,
                    mb: 3,
                    borderRadius: "16px",
                    border: "1px solid",
                    borderColor: "divider",
                    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                }}
            >
                <Grid container spacing={3}>
                    {/* Booking Details */}
                    <Grid item xs={12} md={12} sx={{ mb: 4 }}>
                        <Typography variant="h6" sx={{ mb: 1, fontWeight: "bold" }}>
                            General Information
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            <strong>Booking Id:</strong> {booking?.booking_id || "N/A"}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            <strong>Banquet Venue:</strong> {booking?.banquetType?.banquetName?.name || "N/A"}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            <strong>Party Date:</strong>{" "}
                            {formatDateMoment(booking.bookingDates?.checkIn) || "N/A"}
                            {/* -{" "}
                            {formatDateMoment(booking.bookingDates?.checkOut) || "N/A"} */}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            <strong>Party Time Slot:</strong>{" "}
                            {booking.bookingTime
                                ? `${formatTime(booking.bookingTime.from)} - ${formatTime(
                                    booking.bookingTime.to
                                )}`
                                : "N/A"}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            <strong>Status:</strong>{" "}
                            <Chip
                                label={booking.bookingStatus}
                                color={
                                    booking.bookingStatus === "Confirmed"
                                        ? "success"
                                        : booking.bookingStatus === "Cancelled"
                                            ? "error"
                                            : "default"
                                }
                                size="small"
                            />
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            <strong>Billable Type:</strong>{" "}
                            <Chip
                                label={booking.billable === true ? "Billable" : "Non-Billable"}
                                color={
                                    booking.billable === true
                                        ? "success"
                                        : "default"
                                }
                                size="small"
                            />
                        </Typography>
                        {/* <Typography variant="body1" sx={{ mb: 1 }}>
                            <strong>Payment Status:</strong>{" "}
                            <Chip
                                label={booking.paymentStatus || "N/A"}
                                color={booking.paymentStatus === "Completed" ? "success" : "default"}
                                size="small"
                            />
                        </Typography> */}
                        <Typography variant="body1">
                            <strong>Payment Mode:</strong> {booking?.paymentMode || "N/A"}
                        </Typography>
                    </Grid>

                    {/* Pricing Details */}
                    <Grid item xs={12} md={12}>
                        <Typography variant="h6" sx={{ mb: 1, fontWeight: "bold" }}>
                            Pricing Details
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            <strong>Special Day Charges:</strong> ₹
                            {booking.pricingDetails?.specialDayExtraCharge || 0}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            <strong>Total Amount:</strong> ₹{booking.pricingDetails?.totalAmount || "N/A"}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            <strong>Tax Amount:</strong> ₹{booking.pricingDetails?.totalTaxAmount || "N/A"}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            <strong>Final Total:</strong> ₹{booking.pricingDetails?.final_totalAmount || "N/A"}
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body1" sx={{ mb: 1 }}>
                                <strong>Tax Breakdown:</strong>
                            </Typography>
                            {booking.pricingDetails?.taxTypes?.length > 0 ? (
                                <List dense>
                                    {booking.pricingDetails.taxTypes.map((tax, index) => (
                                        <ListItem key={index}>
                                            <ListItemText
                                                primary={`${tax.taxType}: ₹${tax.taxAmount} (${tax.taxRate}%)`}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            ) : (
                                <Typography variant="body2">No tax details available.</Typography>
                            )}
                        </Box>
                    </Grid>

                    {/* Member Details */}
                    <Grid item xs={12}>
                        <Typography variant="h6" sx={{ mb: 1, fontWeight: "bold" }}>
                            Member Details
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            <strong>Primary Member:</strong> {booking.primaryMemberId?.name || "N/A"}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            <strong>Hosted By:</strong> {booking.invitationOfmember || "N/A"}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            <strong>No of Pax:</strong> {booking.attendingGuests || "N/A"}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            <strong>Mobile:</strong> {booking.mobileNumber || "N/A"}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            <strong>Office Phone Number:</strong> {booking.officePhoneNumber || "N/A"}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            <strong>Residence Phone Number:</strong> {booking.residencePhoneNo || "N/A"}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            <strong>Address:</strong> {booking.address || "N/A"}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            <strong>Occasion:</strong> {booking.occasion || "N/A"}
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Divider sx={{ mb: 2 }} />
                        <Typography variant="body1">
                            <strong>Booking Created Date & Time:</strong>  {booking.createdAt ? formatDateTime(booking.createdAt) : "N/A"}
                        </Typography>
                    </Grid>
                </Grid>

                {/* <Button
                    variant="contained"
                    color="primary"
                    startIcon={<FiEdit />}
                    onClick={handleEditClick}
                    sx={{ mt: 3 }}
                >
                    Edit Booking
                </Button> */}
                {/* Confirm and Cancel Buttons */}
                {/* {booking.bookingStatus === "Pending" && (
                    <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
                        <Button
                            variant="contained"
                            color="success"
                            startIcon={<FiCheck />}
                            onClick={() => handleSaveChanges("Confirmed")}
                        >
                            Confirm
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            startIcon={<FiX />}
                            onClick={() => handleSaveChanges("Cancelled")}
                        >
                            Cancel
                        </Button>
                    </Box>
                )} */}

                {/* {booking.billable && booking.billableDate && booking.bookingStatus === "Pending" && ( */}
                {booking.bookingStatus === "Pending" && (
                    <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
                        <Button
                            variant="contained"
                            color="success"
                            startIcon={<FiCheck />}
                            onClick={() => handleSaveChanges("Confirmed")}
                        >
                            Confirm

                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            startIcon={<FiX />}
                            onClick={() => handleSaveChanges("Cancelled")}
                        >
                            Cancel
                        </Button>
                    </Box>
                )}

            </Paper>

            {/* Edit Dialog */}
            <Dialog open={isEditDialogOpen} onClose={handleDialogClose} fullWidth maxWidth="sm">
                <DialogTitle>Edit Booking Status</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth margin="dense">
                        <InputLabel>Booking Status</InputLabel>
                        <Select
                            name="bookingStatus"
                            value={editBooking.bookingStatus || ""}
                            onChange={handleInputChange}
                        >
                            {statusOptions.map((status) => (
                                <MenuItem key={status} value={status}>
                                    {status}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} color="secondary">
                        Cancel
                    </Button>
                    <Button onClick={handleSaveChanges} color="primary">
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SingleBooking;
